import { BLOG_PILLARS } from "./pillars";
import type { BlogPillar, BlogPostMeta } from "./types";

export const BLOG_PAGE_SIZE = 9;

export type BlogIndexSearch = {
    pillar?: BlogPillar;
    page?: number;
};

/** Drops unknown pillars and non-positive / non-integer pages instead of failing the route. */
export const parseBlogIndexSearch = (search: Record<string, unknown>): BlogIndexSearch => {
    const pillar = BLOG_PILLARS.find((p) => p === search.pillar);
    const page = Number(search.page);
    return {
        ...(pillar && { pillar }),
        ...(Number.isInteger(page) && page > 1 && { page }),
    };
};

export type BlogIndexView = {
    items: BlogPostMeta[];
    /** Pillars that have at least one post, in the canonical pillar order */
    pillars: BlogPillar[];
    pillar: BlogPillar | undefined;
    /** Clamped to the last page when the requested page is out of range */
    page: number;
    totalPages: number;
};

export const buildBlogIndexView = (
    posts: BlogPostMeta[],
    { pillar, page = 1 }: BlogIndexSearch,
): BlogIndexView => {
    const filtered = pillar ? posts.filter((p) => p.pillar === pillar) : posts;
    const totalPages = Math.max(1, Math.ceil(filtered.length / BLOG_PAGE_SIZE));
    const current = Math.min(page, totalPages);
    const start = (current - 1) * BLOG_PAGE_SIZE;

    return {
        items: filtered.slice(start, start + BLOG_PAGE_SIZE),
        pillars: BLOG_PILLARS.filter((p) => posts.some((post) => post.pillar === p)),
        pillar,
        page: current,
        totalPages,
    };
};

/** Site-relative URL for a filter/page combination; the default view is plain `/blog`. */
export const blogIndexHref = ({ pillar, page }: BlogIndexSearch): string => {
    const params = new URLSearchParams();
    if (pillar) params.set("pillar", pillar);
    if (page && page > 1) params.set("page", String(page));
    const query = params.toString();
    return query ? `/blog?${query}` : "/blog";
};
